import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, Wrench, Package, Search, MousePointerClick, Check, ClipboardPlus, Maximize2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { KIT_PARTS, PART_GROUPS, KIT_DIAGRAM_HOTSPOTS, AVIONTEQ_CTRL_LISTING, partById } from '../data/ul101Parts';
import { UL101QuoteTray, useQuoteList } from './UL101QuoteTray';
import { UL101KitChecklist } from './UL101KitChecklist';
import { toast } from 'sonner';
import { trackPartnerContact } from './DistributionPartners';
import { trackEvent } from '../lib/analytics';

const PartCard = ({ part, active, selected, onToggle }) => {
  const toggle = () => {
    if (!selected) trackEvent('part_quote_click', { product: 'UL101', part: part.pn });
    onToggle(part.pn);
  };
  const avq = () => trackPartnerContact('AvionTEq', 'website');
  const href = part.avionteq || AVIONTEQ_CTRL_LISTING;

  return (
    <article id={`part-${part.pn}`} className={`group flex flex-col rounded-2xl border bg-white overflow-hidden scroll-mt-28 transition-[border-color,box-shadow] duration-300 ${active ? 'border-orange-400 shadow-[0_0_0_6px_rgba(249,115,22,0.18)]' : 'border-slate-200 hover:border-blue-300 hover:shadow-lg'}`} data-testid="ul101-part-card" data-pn={part.pn} data-active={active || undefined}>
      <div className="relative h-36 bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-6">
        {part.image ? (
          <img src={part.image} alt={part.name} loading="lazy" className="max-h-28 w-auto max-w-full object-contain group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <Wrench className="h-10 w-10 text-slate-300" aria-hidden="true" />
        )}
        <span className="absolute top-3 left-3 rounded-md bg-[#1e2d3b] px-2 py-0.5 font-mono text-[11px] font-semibold text-white" data-testid="ul101-part-pn">P/N {part.pn}</span>
        {part.set && <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-md bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-700"><Package className="h-3 w-3" /> Set</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-base font-bold text-[#1e2d3b] leading-snug">{part.name}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{part.desc}</p>
        {part.inSet && <p className="mt-2 text-[11px] text-slate-400">Included in set P/N {part.inSet} · also sold individually</p>}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={avq} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 transition-colors" data-testid="ul101-part-avionteq">
            <ExternalLink className="h-3.5 w-3.5" /> {part.avionteq ? 'Buy at AvionTEq' : 'Ask AvionTEq'}
          </a>
          <button type="button" onClick={toggle} aria-pressed={selected} className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${selected ? 'bg-[#1e2d3b] text-white hover:bg-slate-700' : 'bg-orange-500 text-white hover:bg-orange-600'}`} data-testid="ul101-part-quote">
            {selected ? <><Check className="h-3.5 w-3.5" /> In quote list</> : <><ClipboardPlus className="h-3.5 w-3.5" /> Add to quote</>}
          </button>
        </div>
      </div>
    </article>
  );
};

const DiagramHotspots = ({ onPick, large = false, idSuffix = '' }) => (
  <div className="relative inline-block max-w-full">
    <img src="/parts/kit-diagram.webp" alt="Labeled breakout of every component in the CTRL UL101 B00033 kit with CTRL part numbers" loading={large ? 'eager' : 'lazy'} className={`h-auto rounded-lg ${large ? 'max-h-[78vh] w-auto max-w-full' : 'w-full'}`} data-testid={`ul101-kit-diagram${idSuffix}`} />
    {KIT_DIAGRAM_HOTSPOTS.map((h, i) => {
      const part = partById(h.pn);
      if (!part) return null;
      return (
        <button key={`${h.pn}-${i}`} type="button" onClick={() => onPick(h.pn)} title={`P/N ${part.pn} — ${part.name}`} aria-label={`Jump to ${part.name}, part number ${part.pn}`}
          className="group/hs absolute rounded-md border border-transparent hover:border-orange-400 hover:bg-orange-400/15 focus-visible:border-orange-400 focus-visible:bg-orange-400/15 focus:outline-none transition-colors"
          style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.w}%`, height: `${h.h}%` }} data-testid={`ul101-diagram-hotspot-${h.pn}${idSuffix}`}>
          <span className={`pointer-events-none absolute left-1/2 -top-1 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-[#1e2d3b] text-white opacity-0 group-hover/hs:opacity-100 group-focus-visible/hs:opacity-100 transition-opacity ${large ? 'px-2 py-1 text-xs shadow-lg' : 'px-1.5 py-0.5 font-mono text-[10px]'}`}>
            {large ? <><span className="font-mono text-orange-300">{part.pn}</span> · {part.name}</> : part.pn}
          </span>
        </button>
      );
    })}
  </div>
);

const KitDiagram = ({ onPick }) => {
  const [zoom, setZoom] = useState(false);
  const pickFromZoom = (pn) => { setZoom(false); onPick(pn); };
  return (
    <figure className="lg:col-span-5 rounded-2xl border border-slate-200 bg-white p-3">
      <div className="relative">
        <DiagramHotspots onPick={onPick} />
        <button type="button" onClick={() => setZoom(true)} className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-[#1e2d3b]/90 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur hover:bg-orange-500 transition-colors" aria-label="Enlarge kit diagram" data-testid="ul101-kit-diagram-zoom">
          <Maximize2 className="h-3.5 w-3.5" /> Enlarge
        </button>
      </div>
      <figcaption className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
        <MousePointerClick className="h-3 w-3 text-orange-500" aria-hidden="true" /> B00033 kit breakout — tap a component to jump to its part, or enlarge for a closer look
      </figcaption>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-[min(96vw,1500px)] w-auto p-4 sm:p-6 bg-white" data-testid="ul101-kit-diagram-dialog">
          <DialogTitle className="text-base font-bold text-[#1e2d3b] pr-8">B00033 / SPL-1473 kit breakout</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 -mt-2">Hover a component to see its part number — click it to jump to the part card.</DialogDescription>
          <div className="flex justify-center overflow-auto">
            <DiagramHotspots onPick={pickFromZoom} large idSuffix="-large" />
          </div>
        </DialogContent>
      </Dialog>
    </figure>
  );
};

export const UL101KitParts = ({ onRequestQuote }) => {
  const [group, setGroup] = useState('all');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);
  const { list, toggle, setQty, clear } = useQuoteList();
  const { hash } = useLocation();
  const navigate = useNavigate();

  const timers = useRef([]);
  const focusPart = useCallback((pn) => {
    if (!partById(pn)) return;
    timers.current.forEach(clearTimeout);
    setGroup('all');
    setQuery('');
    setActive(pn);
    timers.current = [
      setTimeout(() => document.getElementById(`part-${pn}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120),
      setTimeout(() => setActive((cur) => (cur === pn ? null : cur)), 4000),
    ];
  }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    const m = hash.match(/^#part-([A-Za-z0-9]+)$/);
    if (m) focusPart(m[1]);
  }, [hash, focusPart]);

  const pick = (pn) => {
    trackEvent('part_diagram_click', { product: 'UL101', part: pn });
    if (hash === `#part-${pn}`) focusPart(pn);
    else navigate({ hash: `#part-${pn}` }, { replace: true });
  };

  const parts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return KIT_PARTS.filter((p) => (group === 'all' || p.group === group) && (!q || `${p.pn} ${p.name} ${p.desc}`.toLowerCase().includes(q)));
  }, [group, query]);

  return (
    <section id="kit-parts" className="py-16 sm:py-20 bg-slate-50 border-t border-slate-200 scroll-mt-24" data-testid="ul101-kit-parts">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-10">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Kit components &amp; replacement parts</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] leading-tight">Every B00033 / SPL-1473 component, available individually</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Lost a probe, need a spare headset or a second case? Each component of the UL101 Aviation Troubleshooter kit can be purchased on its own — from our distribution partner <strong className="text-[#1e2d3b]">AvionTEq</strong> or directly from <strong className="text-[#1e2d3b]">CTRL Systems</strong>. Add every part you need to the quote list and send one request.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <UL101KitChecklist onAdded={(n) => toast.success(`${n} missing part${n === 1 ? '' : 's'} added to your quote list`)} />
              <span className="text-xs text-slate-500">Tick what's in your case — reorder the rest in one go.</span>
            </div>
          </div>
          <KitDiagram onPick={pick} />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Part groups">
            {PART_GROUPS.map((g) => (
              <button key={g.id} type="button" role="tab" aria-selected={group === g.id} onClick={() => setGroup(g.id)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${group === g.id ? 'bg-[#1e2d3b] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700'}`} data-testid={`ul101-parts-filter-${g.id}`}>
                {g.label}
              </button>
            ))}
          </div>
          <div className="relative md:ml-auto md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search part number or name…" className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="ul101-parts-search" />
          </div>
        </div>

        <p className="mb-4 text-sm text-slate-500" data-testid="ul101-parts-count"><span className="font-bold text-[#1e2d3b]">{parts.length}</span> part{parts.length === 1 ? '' : 's'}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {parts.map((p) => <PartCard key={p.pn} part={p} active={active === p.pn} selected={!!list[p.pn]} onToggle={toggle} />)}
        </div>
        {!parts.length && <p className="py-10 text-center text-sm italic text-slate-400" data-testid="ul101-parts-empty">No parts match that search.</p>}

        <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-3xl">
          Pricing and availability for individual parts are provided by AvionTEq or on quote from CTRL Systems. Add as many parts as you need to the quote list and send them to CTRL in a single request. The B00033 kit ships with the intrinsically safe receiver and transmitter — check the label on your existing unit before ordering.
        </p>
      </div>
      <UL101QuoteTray list={list} setQty={setQty} clear={clear} onRequestQuote={onRequestQuote} />
    </section>
  );
};
